import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getNurses, getReportSheets, reassignReport } from '../../api/dashboard';
import { errorCode } from '../../api/client';
import ConfirmDialog from '../../components/ConfirmDialog';
import PageHeader from '../../components/PageHeader';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import StatusMessage from '../../components/StatusMessage';
import { formatShortDate, patientMrn, patientSummary } from '../../utils/format';
import { filterPatients } from '../patientListUtils';
import { adminErrorMessage } from './adminErrors';
import { groupByNurse, moveSheet } from './boardUtils';
import './Admin.css';
import './ReportBoard.css';

const initial = (name) => (name || '?').trim().charAt(0).toUpperCase();
// "Room 402, MRN 00123" for the reassign dialog; either can be missing.
const sheetDetails = (sheet) => [sheet.room && `Room ${sheet.room}`, patientMrn(sheet)].filter(Boolean).join(', ');

// One patient as an Asana-style task card: name, a grey summary line, and a
// room tag with the MRN and when the sheet was last updated. The nurse is the
// column.
function BoardCard({ sheet }) {
  const { room, ...rest } = sheet;
  const summary = patientSummary(rest);
  const mrn = patientMrn(sheet);
  const updated = formatShortDate(sheet.updatedAt);
  return (
    <Link
      to={`/reports/${encodeURIComponent(sheet.reportId)}`}
      state={{ patient: { name: sheet.name, room: sheet.room }, nurseName: sheet.owner?.name }}
      className="board-task"
      draggable={false}
    >
      <span className="board-task-name">{sheet.name || 'Unnamed patient'}</span>
      {summary && <span className="board-task-summary">{summary}</span>}
      <span className="board-task-meta">
        {room && String(room).trim().toLowerCase() !== 'none' && <span className="board-task-tag">Room {room}</span>}
        {mrn && <span className="board-task-date">{mrn}</span>}
        {updated && <span className="board-task-date">Updated {updated}</span>}
      </span>
    </Link>
  );
}

// The ⋯ menu on a card: the keyboard-friendly way to reassign, and the only
// way to reach a nurse who has no column yet because they have no patients.
function ReassignMenu({ sheet, nurses, onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const targets = nurses.filter((n) => n.id && n.id !== sheet.owner?.id);
  return (
    <div className="board-menu" ref={ref}>
      <button
        type="button"
        className="board-menu-button"
        aria-label={`Reassign ${sheet.name || 'patient'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        ⋯
      </button>
      {open && (
        <div className="board-menu-list" role="menu" onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
          <p className="board-menu-label">Reassign to</p>
          {targets.length === 0 && <p className="board-menu-empty">No other nurses on your unit.</p>}
          {targets.map((n) => (
            <button key={n.id} type="button" role="menuitem" className="board-menu-item"
              onClick={() => { setOpen(false); onPick(sheet, n); }}>
              {n.name || n.email}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Charge nurses only: their unit's current report sheets as an Asana-style
// board, one column per nurse on the unit. Dragging a card to another nurse's
// column (or using its ⋯ menu) reassigns the patient after a confirmation.
// A nurse's patients are always on their own unit, so every patient is in
// one of these columns.
const ReportSheets = () => {
  const [sheets, setSheets] = useState(null);
  // The unit's nurses: the board's columns. null until loaded, or if the
  // roster can't be loaded (then columns fall back to the sheets' owners).
  const [nurses, setNurses] = useState(null);
  const myEmail = useAuth()?.nurse?.email;
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [dragId, setDragId] = useState(null);
  const [overColumn, setOverColumn] = useState(null);
  const [pending, setPending] = useState(null); // { sheet, to }
  const [saving, setSaving] = useState(false);
  // After a move: { kind: 'moved', name, reportId, from, to, canUndo } until
  // closed, undone or the next move; or { kind: 'error', text }.
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getReportSheets();
      setSheets((data.units || []).flatMap((u) => u.reportSheets || []));
    } catch (err) {
      // 401 is handled globally (sign-out).
      if (errorCode(err) !== 'unauthenticated') setError(errorCode(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    getNurses().then((res) => setNurses(res.nurses || [])).catch(() => setNurses(null));
  }, [load]);


  const columns = useMemo(() => groupByNurse(sheets || [], nurses, myEmail), [sheets, nurses, myEmail]);
  const total = sheets?.length || 0;
  const shown = columns.reduce((sum, c) => sum + filterPatients(c.sheets, query).length, 0);

  const askToMove = (sheet, to) => {
    if (!to?.id || to.id === sheet.owner?.id) return;
    setNotice(null);
    setPending({ sheet, to });
  };

  // Moves a sheet to another nurse: the card moves right away and moves back
  // if the backend refuses. Returns whether it worked.
  const move = async (sheet, to) => {
    const before = sheets;
    setSheets((prev) => moveSheet(prev, sheet.reportId, to));
    try {
      const updated = await reassignReport(sheet.reportId, to.id);
      if (updated?.reportId) setSheets((prev) => prev.map((s) => (s.reportId === updated.reportId ? updated : s)));
      return true;
    } catch (err) {
      setSheets(before);
      setNotice({ kind: 'error', text: adminErrorMessage(err, "Couldn't reassign this patient. Please try again.") });
      return false;
    }
  };

  const confirmMove = async () => {
    const { sheet, to } = pending;
    const from = { id: sheet.owner?.id, name: sheet.owner?.name || 'no one', isMine: Boolean(sheet.owner?.isMine) };
    // Undo moves the patient back, so only offer it when the previous nurse is
    // one of the unit's nurses (a nurse not on the unit can't receive patients).
    const fromColumn = columns.find((c) => c.id === from.id);
    setSaving(true);
    if (await move(sheet, to)) {
      setNotice({
        kind: 'moved', name: sheet.name || 'The patient', reportId: sheet.reportId, from, to,
        canUndo: Boolean(fromColumn && !fromColumn.notOnUnit),
      });
    }
    setSaving(false);
    setPending(null);
  };

  // Undo restores the earlier owner, so it needs no second confirmation.
  const undo = async () => {
    const { reportId, name, from, to } = notice;
    const sheet = sheets.find((s) => s.reportId === reportId);
    setNotice(null);
    if (sheet && await move(sheet, from)) {
      setNotice({ kind: 'moved', name, reportId, from: to, to: from, canUndo: false, undone: true });
    }
  };

  const dropOn = (column) => (e) => {
    e.preventDefault();
    setOverColumn(null);
    const sheet = sheets.find((s) => s.reportId === (dragId || e.dataTransfer.getData('text/plain')));
    setDragId(null);
    // A nurse who isn't on the unit can't be given patients.
    if (sheet && !column.notOnUnit) askToMove(sheet, { id: column.id, name: column.name, isMine: column.isMine });
  };

  return (
    <>
      <PageHeader
        title="Report Sheets"
        actions={(
          <button type="button" className="title-button" onClick={load} disabled={loading}>
            {loading && sheets ? 'Refreshing…' : 'Refresh'}
          </button>
        )}
      />
      <div className="component-container">
        <div className="admin-content board-content">
          {error && (
            <StatusMessage kind="error" onRetry={load}>
              {error === 'network_error'
                ? "Can't reach the dashboard server. Check your connection."
                : "We couldn't load your unit's report sheets."}
            </StatusMessage>
          )}
          {!error && !sheets && <StatusMessage kind="loading">Loading report sheets…</StatusMessage>}
          {sheets && (
            <>
              <div className="search-row">
                <input
                  type="search"
                  className="search-input"
                  placeholder="Search by patient name or room"
                  aria-label="Search report sheets"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <span className="search-count">
                  {query ? `${shown} of ${total}` : total} current {total === 1 ? 'patient' : 'patients'}
                </span>
                <span className="board-hint">Drag a patient to another nurse to reassign them.</span>
              </div>

              {notice?.kind === 'error' && <div className="error-banner" role="alert">{notice.text}</div>}
              {notice?.kind === 'moved' && (
                <div className="board-moved" role="status">
                  <span className="board-moved-check" aria-hidden="true">✓</span>
                  <span className="board-moved-text">
                    {notice.undone ? 'Undone: ' : ''}<strong>{notice.name}</strong> moved from <strong>{notice.from.name}</strong> to{' '}
                    <strong>{notice.to.name}</strong>.
                  </span>
                  {notice.canUndo && (
                    <button type="button" className="board-moved-undo" onClick={undo}>Undo</button>
                  )}
                  <button type="button" className="board-moved-close" onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
                </div>
              )}

              {total === 0 && <StatusMessage kind="empty">No current report sheets on your unit.</StatusMessage>}

              {total > 0 && (
                <div className="board" aria-label="Report sheets by nurse">
                  {columns.map((column) => {
                    const visible = filterPatients(column.sheets, query);
                    return (
                      <section
                        key={column.id}
                        className={`board-column${overColumn === column.id ? ' board-column-over' : ''}${column.notOnUnit ? ' board-column-off-unit' : ''}`}
                        aria-label={column.name}
                        onDragOver={column.notOnUnit ? undefined : (e) => { e.preventDefault(); setOverColumn(column.id); }}
                        onDragLeave={column.notOnUnit ? undefined : (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOverColumn(null); }}
                        onDrop={column.notOnUnit ? undefined : dropOn(column)}
                      >
                        <header className="board-column-header">
                          <span className="board-avatar" aria-hidden="true">{initial(column.name)}</span>
                          <h2 className="board-column-name">{column.name}</h2>
                          <span className="board-column-count">{column.sheets.length}</span>
                          {column.isMine && <span className="board-you">You</span>}
                          {column.notOnUnit && <span className="board-off-unit-tag">Not on this unit</span>}
                        </header>
                        <div className="board-column-body">
                          {column.notOnUnit && (
                            <p className="board-off-unit-note" role="note">
                              {column.name} is no longer assigned to this unit. Reassign these patients to a nurse on your unit.
                            </p>
                          )}
                          {visible.length === 0 && (
                            <p className="board-empty">{column.sheets.length === 0 ? 'No patients' : 'No matches'}</p>
                          )}
                          {visible.map((sheet) => (
                            <div
                              key={sheet.reportId}
                              className={`board-card${dragId === sheet.reportId ? ' board-card-dragging' : ''}`}
                              draggable
                              onDragStart={(e) => {
                                setDragId(sheet.reportId);
                                e.dataTransfer.effectAllowed = 'move';
                                e.dataTransfer.setData('text/plain', sheet.reportId);
                              }}
                              onDragEnd={() => { setDragId(null); setOverColumn(null); }}
                            >
                              <BoardCard sheet={sheet} />
                              <ReassignMenu sheet={sheet} nurses={nurses || []} onPick={askToMove} />
                            </div>
                          ))}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {pending && (
        <ConfirmDialog
          title="Reassign patient?"
          confirmLabel="Reassign"
          busy={saving}
          onConfirm={confirmMove}
          onCancel={() => setPending(null)}
        >
          <p>
            Reassign <strong>{pending.sheet.name || 'this patient'}</strong>
            {sheetDetails(pending.sheet) ? ` (${sheetDetails(pending.sheet)})` : ''} from{' '}
            <strong>{pending.sheet.owner?.name || 'no one'}</strong> to <strong>{pending.to.name}</strong>?
          </p>
          <p>{pending.to.name} will see this patient in the Aren app.</p>
        </ConfirmDialog>
      )}
    </>
  );
};

export default ReportSheets;
